import { useEffect, useState } from "react"
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material"

import { billingApi } from "../../api"
import { LoadingRows } from "../../components/LoadingState"
import { money } from "./InvoiceTable"
import { surchargeCents } from "./surcharge"

/**
 * What this client is billed on: currency, terms, tax, and the card fee.
 *
 * ── Why the card fee is a preset and not a number ───────────────────────────
 *
 * Stripe's price is not one rate. A card issued in the account's own country is
 * 2.9% + 30c; one issued abroad adds 1.5%; charging in the client's currency
 * adds another 1%; Invoicing takes 0.4% of what it collects. Those stack, so
 * the same invoice costs 3.3% to collect from one client and 5.8% from another
 * — and picking a single number for everybody means under-recovering on exactly
 * the clients who cost the most.
 *
 * The presets are that table, from the server, so the components stay in one
 * place when Stripe changes them. The field underneath stays editable, because
 * a rate somebody has negotiated is a fact this app does not know.
 *
 * ── The thing this screen deliberately does not decide ──────────────────────
 *
 * Whether passing a card fee on is permitted at all depends on where the client
 * is and what card they hold, and the card networks have their own rules about
 * disclosure and ceilings. That is not a judgement software can make from a
 * currency code, so the screen states the cost plainly and leaves the decision
 * where it belongs.
 */
export default function BillingSettingsDialog({ open, clientId, clientLabel, onClose, onSaved }) {
  const [settings, setSettings] = useState(null)
  const [presets, setPresets] = useState([])
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!open || !clientId) return
    setLoading(true)
    setError(null)
    Promise.all([billingApi.settings(clientId), billingApi.status()])
      .then(([settingsData, statusData]) => {
        setSettings(settingsData.settings || null)
        setPresets(statusData.fees?.presets || [])
      })
      .catch((err) => setError(err.userMessage))
      .finally(() => setLoading(false))
  }, [open, clientId])

  const set = (patch) => setSettings((current) => ({ ...current, ...patch }))

  const save = async (event) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await billingApi.saveSettings(clientId, {
        currency: settings.currency,
        payment_terms_days: Number(settings.payment_terms_days) || 0,
        tax_rate_bps: Math.round(Number(settings.tax_rate_bps) || 0),
        surcharge_bps: Math.round(Number(settings.surcharge_bps) || 0),
        surcharge_fixed_cents: Math.round(Number(settings.surcharge_fixed_cents) || 0),
        surcharge_label: settings.surcharge_label || null,
        billing_email: settings.billing_email || null,
      })
      onSaved(`Billing settings saved for ${clientLabel || "this client"}.`)
    } catch (err) {
      setError(err.userMessage)
    } finally {
      setBusy(false)
    }
  }

  const rate = {
    surchargeBps: Number(settings?.surcharge_bps) || 0,
    surchargeFixedCents: Number(settings?.surcharge_fixed_cents) || 0,
  }
  /* A hundred dollars, because it is the number anybody can check in their head
     against the percentage they just typed. */
  const example = surchargeCents(10000, rate)

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <form onSubmit={save}>
        <DialogTitle>
          Billing settings
          <Typography variant="caption" color="text.secondary" display="block">
            {clientLabel}
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}
          {loading || !settings ? (
            <LoadingRows rows={4} />
          ) : (
            <>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    label="Currency"
                    value={settings.currency || "USD"}
                    onChange={(e) => set({ currency: e.target.value.toUpperCase().slice(0, 3) })}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    label="Payment terms"
                    type="number"
                    value={settings.payment_terms_days ?? 14}
                    onChange={(e) => set({ payment_terms_days: e.target.value })}
                    fullWidth
                    helperText="Days"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    label="Tax"
                    type="number"
                    value={((Number(settings.tax_rate_bps) || 0) / 100).toString()}
                    onChange={(e) => set({ tax_rate_bps: Math.round((Number(e.target.value) || 0) * 100) })}
                    fullWidth
                    slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    label="Where invoices go"
                    value={settings.billing_email || ""}
                    onChange={(e) => set({ billing_email: e.target.value })}
                    fullWidth
                    helperText="Falls back to the client's administrator"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2.5 }} />

              <Typography variant="body2" fontWeight={650} gutterBottom>
                Card fee passed to this client
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                What Stripe takes to collect by card. It is added to the invoice, grossed up so the fee on the fee is
                covered too — and it is a tick box on each invoice, so one that is paid another way does not carry it.
              </Typography>

              {presets.length > 0 ? (
                <TextField
                  select
                  label="Start from"
                  value=""
                  onChange={(e) => {
                    const chosen = presets.find((p) => p.id === e.target.value)
                    if (chosen) set({ surcharge_bps: chosen.bps, surcharge_fixed_cents: chosen.fixed_cents })
                  }}
                  fullWidth
                  size="small"
                  sx={{ mb: 2 }}
                  slotProps={{ select: { displayEmpty: true } }}
                  helperText="Stripe's published rates. Adjust below if yours are negotiated."
                >
                  <MenuItem value="">Choose how this client pays…</MenuItem>
                  {presets.map((preset) => (
                    <MenuItem key={preset.id} value={preset.id}>
                      {preset.label} — {(preset.bps / 100).toFixed(2).replace(/\.?0+$/, "")}% +{" "}
                      {money(preset.fixed_cents, settings.currency || "USD")}
                    </MenuItem>
                  ))}
                </TextField>
              ) : null}

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    label="Percentage"
                    type="number"
                    value={((Number(settings.surcharge_bps) || 0) / 100).toString()}
                    onChange={(e) => set({ surcharge_bps: Math.round((Number(e.target.value) || 0) * 100) })}
                    fullWidth
                    slotProps={{
                      input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
                      htmlInput: { step: "0.1", min: 0, max: 10 },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    label="Per transaction"
                    type="number"
                    value={((Number(settings.surcharge_fixed_cents) || 0) / 100).toString()}
                    onChange={(e) => set({ surcharge_fixed_cents: Math.round((Number(e.target.value) || 0) * 100) })}
                    fullWidth
                    slotProps={{ htmlInput: { step: "0.01", min: 0 } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    label="Shown as"
                    value={settings.surcharge_label || ""}
                    onChange={(e) => set({ surcharge_label: e.target.value })}
                    fullWidth
                    placeholder="Card processing fee"
                  />
                </Grid>
              </Grid>

              {/* The arithmetic, on a number anybody can check in their head. */}
              <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  On a {money(10000, settings.currency || "USD")} invoice
                </Typography>
                <Typography variant="caption" fontWeight={650}>
                  {example > 0
                    ? `+ ${money(example, settings.currency || "USD")} → ${money(10000 + example, settings.currency || "USD")}`
                    : "no fee added"}
                </Typography>
              </Stack>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={busy} color="inherit">
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={busy || loading || !settings}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
